import ComplianceQuestionAssignment from "@/components/probaworkflows_base_components/ComplianceQuestionAssignment/ComplianceQuestionAssignment.vue";
import {
  CREATE_COMPLIANCE_RULE_URL,
  DELETE_COMPLIANCE_RULE_URL,
  GET_COMPLIANCE_DROPDOWNS_URL,
  GET_COMPLIANCE_RULES,
  GET_CSRF_TOKEN,
  GET_ENTITY_COMPLIANCE_COUNTDOWN,
  get_headers,
  START_COMPLIANCE_COUNTDOWN,
  UPDATE_COMPLIANCE_RULE_URL,
} from "@/utils/constants/constants";
import alert_mixin from "@/utils/mixins/alert_mixin";
import axios from "axios";
import "datatables.net";
import "datatables.net-dt/css/dataTables.dataTables.css";
import $ from "jquery";

export default {
  name: "ComplianceRules",
  mixins: [alert_mixin],
  components: {
    ComplianceQuestionAssignment,
  },

  props: {
    form_id: {
      type: [String, Number],
      required: true,
    },
    form_name: {
      type: String,
      default: "",
    },
  },

  data: () => ({
    token: null,
    headers: get_headers(),

    loading: true,
    is_creating_rule: false,
    is_deleting: false,
    is_editing: false,

    /* RULE DATA */
    rules: [],
    rule_to_delete: null,
    countdown_data: [],

    /* ENTITY TYPE */
    entity_type_id: null,

    /* QUESTION ASSIGNMENT MODAL */
    selected_rule: null,
    show_assign_modal: false,

    /* DataTable instance */
    dataTable: null,

    /* Modal instances */
    createRuleModal: null,
    deleteConfirmationModal: null,

    /* Modal visibility states */
    show_create_modal: false,
    show_delete_modal: false,

    rule_form: {
      id: null,
      name: "",
      description: "",
      initial_grace_period: "",
      frequency: "",
      recurring_grace_period: "",
      entity_type: null,
      version: 1,
      initial_grace_period_days: null,
      recurring_grace_period_days: null,
      frequency_type: null,
      custom_frequency_days: null,
      trigger_status_ids: [],
    },
    form_status_options: [],
  }),
  mounted() {
    this.$nextTick(() => {
      this.initialize_modals();
    });
  },

  created() {
    this.initializeToken().then(() => {
      this.entity_type_id = sessionStorage.getItem("entity_type_id");
      this.rule_form.entity_type = this.entity_type_id;
      this.fetch_form_statuses();
      this.get_compliance_rules();
    }).catch(error => {
      this.show_error("Failed to initialize token: " + error.message);
    });
  },

  computed: {
    table_safe_rule_list() {
      return Array.isArray(this.rules) ? this.rules : [];
    },

    calculated_frequency_days() {
      if (this.rule_form.frequency_type === "custom") {
        return this.rule_form.custom_frequency_days || 0;
      }

      const frequencyMap = {
        "weekly": 7,
        "biweekly": 14,
        "monthly": 30,
        "quarterly": 90,
        "biannually": 180,
        "annually": 365
      };

      return Reflect.get(frequencyMap, this.rule_form.frequency_type) || 30;
    },
  },

  methods: {
    /* ---------------- TOKEN MANAGEMENT ---------------- */
    async initializeToken() {
      const response = await axios.get(GET_CSRF_TOKEN, {
        headers: this.headers
      });

      // Handle multiple possible response formats
      if (response.data && response.data.csrf_token) {
        // Format 1: { "csrf_token": "token_value" }
        this.token = response.data.csrf_token;
      } else if (response.data && response.data.csrfToken) {
        // Format 2: { "csrfToken": "token_value" }
        this.token = response.data.csrfToken;
      } else if (response.data && response.data.token) {
        // Format 3: { "token": "token_value" }
        this.token = response.data.token;
      } else if (response.data && typeof response.data === 'string') {
        // Format 4: Direct token string
        this.token = response.data;
      } else if (response.data) {
        // Format 5: Try to find token in any nested structure
        this.token = this.findTokenInObject(response.data);
      }

      // Update headers with token if found
      if (this.token) {
        this.headers = {
          ...this.headers,
          'X-CSRFToken': this.token
        };
      } else {
        // If no token in response, try cookies
        this.token = this.getTokenFromCookies();
        if (this.token) {
          this.headers = {
            ...this.headers,
            'X-CSRFToken': this.token
          };
        } else {
          this.show_error("No CSRF token found in response or cookies");
        }
      }
    },

    findTokenInObject(obj) {
      if (typeof obj !== "object" || obj === null) return null;

      // Common CSRF / token keys
      const tokenKeys = ["csrf_token", "csrfToken", "token", "csrftoken", "csrf"];

      for (const key of tokenKeys) {
        if (Reflect.has(obj, key)) {
          const tokenValue = Reflect.get(obj, key);
          if (tokenValue) return tokenValue;
        }
      }

      // Recursively search own enumerable properties only
      const keys = Object.keys(obj);
      for (const key of keys) {
        // Explicit own-property check (Codacy-friendly)
        if (!Reflect.has(obj, key)) continue;

        const value = Reflect.get(obj, key);
        if (value && typeof value === "object") {
          const found = this.findTokenInObject(value);
          if (found) return found;
        }
      }

      return null;
    },

    getTokenFromCookies() {
      const name = 'csrftoken';
      if (!document.cookie || document.cookie === '') {
        return null;
      }

      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const trimmedCookie = cookie.trim();
        if (trimmedCookie.startsWith(name + '=')) {
          return decodeURIComponent(trimmedCookie.substring(name.length + 1));
        }
      }

      return null;
    },

    async refreshToken() {
      await this.initializeToken();
      this.show_success("Token refreshed successfully");
    },

    /* ---------------- CONDITIONS PAGE NAVIGATION ---------------- */
    open_conditions_page(rule) {
      sessionStorage.setItem('current_rule_id', rule.id);
      sessionStorage.setItem('current_rule_name', rule.name);
      sessionStorage.setItem('current_form_id', this.current_form_id);
      sessionStorage.setItem('current_form_name', this.current_form_name);

      this.$router.push({
        path: '/condition_management',
        query: {
          rule_id: rule.id,
          rule_name: rule.name,
          form_id: this.current_form_id,
          form_name: this.current_form_name
        }
      });
    },

    /* ---------------- DURATION CONVERSION METHODS ---------------- */
    daysToDuration(days) {
      if (days === null || days === undefined) return null;
      if (days === 0) return "0 days";
      return `${days} days`;
    },

    durationToDays(durationString) {
      if (!durationString) return 0;

      const match = durationString.match(/(\d+)\s*days?/i);
      if (match) {
        return parseInt(match[1], 10);
      }

      const daysMatch = durationString.match(/^(\d+)\s+(\d+):(\d+):(\d+)$/);
      if (daysMatch) {
        return parseInt(daysMatch[1], 10);
      }

      const timeMatch = durationString.match(/^(\d+):(\d+):(\d+)$/);
      if (timeMatch) {
        const hours = parseInt(timeMatch[1], 10);
        return Math.round(hours / 24);
      }

      return 0;
    },

    getFrequencyTypeFromDays(days) {
      const frequencyMap = new Map([
        [7, "weekly"],
        [14, "biweekly"],
        [30, "monthly"],
        [90, "quarterly"],
        [180, "biannually"],
        [365, "annually"]
      ]);

      return frequencyMap.get(days) || "custom";
    },

    /* ---------------- FETCH RULES ---------------- */
    get_compliance_rules() {
      this.loading = true;

      axios.get(GET_COMPLIANCE_RULES, {
        headers: this.headers,
        params: {
          entity_type_id: this.entity_type_id,
          form_id: this.form_id,
        },
      })
        .then((response) => {
          this.rules = response.data?.rules || [];

          // Merge countdown data with rules if available
          this.merge_countdown_with_rules();

          this.$nextTick(() => {
            this.initialize_data_table();
          });
        })
        .catch((error) => {
          this.show_error("Error fetching compliance rules: " + error.message);
          if (error.response && error.response.status === 403) {
            this.refreshToken();
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /* ---------------- MERGE COUNTDOWN DATA ---------------- */
    merge_countdown_with_rules() {
      if (this.countdown_data.length > 0) {
        const merged_rules = this.rules.map(rule => {
          const countdown = this.countdown_data.find(c => c.rule_id === rule.id);
          const merged_rule = {
            ...rule,
            days_remaining: countdown?.days_remaining,
            countdown_status: countdown?.status,
            next_eval_date: countdown?.next_eval_date,
          };
          return merged_rule;
        });
        this.rules = merged_rules;
        return merged_rules;
      }
    },

    /* ---------------- GET COUNTDOWN STATUS COLOR ---------------- */
    get_countdown_badge_class(days_remaining, countdown_status) {
      if (!days_remaining && days_remaining !== 0) return 'badge bg-secondary';
      if (countdown_status === 'Overdue' || days_remaining < 0) return 'badge bg-danger';
      if (days_remaining <= 7) return 'badge bg-warning text-dark';
      if (days_remaining <= 14) return 'badge bg-info';
      return 'badge bg-success';
    },

    /* ---------------- FORMAT COUNTDOWN DISPLAY ---------------- */
    format_countdown_display(days_remaining, countdown_status) {
      if (!days_remaining && days_remaining !== 0) {
        return countdown_status || 'Not Started';
      }
      if (days_remaining < 0) {
        return `Overdue by ${Math.abs(days_remaining)} days`;
      }
      if (days_remaining === 0) {
        return 'Due Today';
      }
      return `${days_remaining} days left`;
    },

    /* ---------------- QUESTION ASSIGNMENT MODAL ---------------- */
    open_assign_questions_modal(rule) {
      this.selected_rule = rule;
      this.show_assign_modal = true;
    },

    close_assign_questions_modal() {
      this.show_assign_modal = false;
      this.selected_rule = null;
    },

    /* ---------------- REFRESH COUNTDOWN DATA ---------------- */
    async refresh_countdown_data() {
      // Get entity_profile_id from session storage or component props
      const entity_profile_id = sessionStorage.getItem('entity_profile_id');

      if (entity_profile_id) {
        this.loading = true;
        await this.get_entity_compliance_countdown(entity_profile_id);
        this.$nextTick(() => {
          this.initialize_data_table();
        });
        this.loading = false;
        this.show_success('Countdown data refreshed');
      } else {
        this.show_info('Entity profile not selected. Countdown data will be available when viewing specific entity.');
      }
    },

    /* ---------------- MODAL INITIALIZATION ---------------- */
    initialize_modals() {
      const createRuleModalEl = this.$refs.createRuleModal;
      const deleteConfirmationModalEl = this.$refs.deleteConfirmationModal;

      if (createRuleModalEl && window.bootstrap) {
        this.createRuleModal = new window.bootstrap.Modal(createRuleModalEl, {
          backdrop: true,
          keyboard: true,
        });
      }

      if (deleteConfirmationModalEl && window.bootstrap) {
        this.deleteConfirmationModal = new window.bootstrap.Modal(deleteConfirmationModalEl, {
          backdrop: true,
          keyboard: true,
        });
      }
    },

    /* ---------------- MODAL FUNCTIONS ---------------- */
    open_create_rule_modal() {
      this.is_editing = false;
      this.reset_rule_form();

      if (this.createRuleModal) {
        this.createRuleModal.show();
      }
    },

    open_edit_rule_modal(rule) {
      this.is_editing = true;
      this.populate_rule_form(rule);

      if (this.createRuleModal) {
        this.createRuleModal.show();
      }
    },

    close_rule_modal() {
      if (this.createRuleModal) {
        this.createRuleModal.hide();
      }
      this.reset_rule_form();
      this.is_editing = false;
    },

    /* ---------------- DELETE MODAL FUNCTIONS ---------------- */
    confirm_delete_rule(rule) {
      this.rule_to_delete = rule;

      if (this.deleteConfirmationModal) {
        this.deleteConfirmationModal.show();
      }
    },

    close_delete_modal() {
      if (this.deleteConfirmationModal) {
        this.deleteConfirmationModal.hide();
        window.location.reload();
      }
      this.rule_to_delete = null;
    },

    /* ---------------- POPULATE FORM FOR EDITING ---------------- */
    populate_rule_form(rule) {
      const initialGraceDays = this.durationToDays(rule.initial_grace_period);
      const recurringGraceDays = this.durationToDays(rule.recurring_grace_period);
      const frequencyDays = this.durationToDays(rule.frequency);

      this.rule_form = {
        id: rule.id,
        name: rule.name || "",
        description: rule.description || "",
        initial_grace_period: "",
        frequency: "",
        recurring_grace_period: "",
        entity_type: rule.entity_type || sessionStorage.getItem("entity_type_id"),
        version: rule.version || 1,
        initial_grace_period_days: initialGraceDays,
        recurring_grace_period_days: recurringGraceDays,
        frequency_type: this.getFrequencyTypeFromDays(frequencyDays),
        custom_frequency_days: this.getFrequencyTypeFromDays(frequencyDays) === "custom" ? frequencyDays : null,
        trigger_status_ids: (rule.trigger_statuses || []).map(ts => ts.id),
      };
    },

    /* ---------------- FETCH ENTITY COMPLIANCE COUNTDOWN ---------------- */
    async get_entity_compliance_countdown(entity_profile_id) {
      if (!entity_profile_id) {
        return;
      }

      return axios.get(GET_ENTITY_COMPLIANCE_COUNTDOWN, {
        headers: this.headers,
        params: {
          entity_profile_id: entity_profile_id
        }
      })
        .then((response) => {
          if (response.data?.status === 'success') {
            this.countdown_data = response.data.compliance_countdown || [];
            this.merge_countdown_with_rules();
          }
        })
        .catch(() => {
          // Don't show error to user, countdown is optional feature
        });
    },

    /* ---------------- START COMPLIANCE COUNTDOWN ---------------- */
    async start_compliance_countdown(entity_profile_id, rule_ids = null) {
      if (!entity_profile_id) {
      return;
      }

      const payload = {
      entity_profile_id: entity_profile_id
      };

      if (rule_ids) {
      payload.compliance_rule_ids = Array.isArray(rule_ids) ? rule_ids : [rule_ids];
      }

      const response = await axios.post(START_COMPLIANCE_COUNTDOWN, payload, {
      headers: this.headers
      });

      if (response.data?.status === 'success') {
      // Refresh countdown data after starting
      await this.get_entity_compliance_countdown(entity_profile_id);
      }
    },

    /* ---------------- CREATE RULE ---------------- */
    create_rule() {
      if (this.is_creating_rule) return;

      if (!this.token) {
        this.show_error("Authentication token is not available. Please refresh the page.");
        return;
      }

      if (!this.rule_form.name.trim()) {
        this.show_error("Please enter a rule name.");
        return;
      }

      if (this.rule_form.initial_grace_period_days < 0) {
        this.show_error("Initial grace period cannot be negative.");
        return;
      }

      if (this.rule_form.recurring_grace_period_days < 0) {
        this.show_error("Recurring grace period cannot be negative.");
        return;
      }

      if (this.rule_form.frequency_type === "custom" && (!this.rule_form.custom_frequency_days || this.rule_form.custom_frequency_days < 1)) {
        this.show_error("Please enter a valid custom frequency (minimum 1 day).");
        return;
      }

      this.is_creating_rule = true;

      const payload = {
        name: this.rule_form.name,
        description: this.rule_form.description,
        initial_grace_period: this.daysToDuration(this.rule_form.initial_grace_period_days),
        recurring_grace_period: this.daysToDuration(this.rule_form.recurring_grace_period_days),
        frequency: this.daysToDuration(this.calculated_frequency_days),
        entity_type: this.rule_form.entity_type,
        version: this.rule_form.version,
        form_id: this.current_form_id,
        trigger_status_ids: this.rule_form.trigger_status_ids,
      };

      axios
        .post(CREATE_COMPLIANCE_RULE_URL, payload, {
          headers: this.headers,
        })
        .then(() => {
          this.show_success("Compliance rule created successfully");
          this.close_rule_modal();
          window.location.reload();
        })
        .catch((error) => {
          this.show_error(
            error.response?.data?.message || "Failed to create rule"
          );
          if (error.response && error.response.status === 403) {
            this.refreshToken();
          }
        })
        .finally(() => {
          this.is_creating_rule = false;
        });
    },

    /* ---------------- UPDATE RULE ---------------- */
    update_rule() {
      if (this.is_creating_rule || !this.rule_form.id) return;

      if (!this.token) {
        this.show_error("Authentication token is not available. Please refresh the page.");
        return;
      }

      if (!this.rule_form.name.trim()) {
        this.show_error("Please enter a rule name.");
        return;
      }

      if (this.rule_form.initial_grace_period_days < 0) {
        this.show_error("Initial grace period cannot be negative.");
        return;
      }

      if (this.rule_form.recurring_grace_period_days < 0) {
        this.show_error("Recurring grace period cannot be negative.");
        return;
      }

      if (this.rule_form.frequency_type === "custom" && (!this.rule_form.custom_frequency_days || this.rule_form.custom_frequency_days < 1)) {
        this.show_error("Please enter a valid custom frequency (minimum 1 day).");
        return;
      }

      this.is_creating_rule = true;

      const payload = {
        id: this.rule_form.id,
        name: this.rule_form.name,
        description: this.rule_form.description,
        initial_grace_period: this.daysToDuration(this.rule_form.initial_grace_period_days),
        recurring_grace_period: this.daysToDuration(this.rule_form.recurring_grace_period_days),
        frequency: this.daysToDuration(this.calculated_frequency_days),
        entity_type: this.rule_form.entity_type,
        version: this.rule_form.version,
        form_id: this.current_form_id,
        trigger_status_ids: this.rule_form.trigger_status_ids,
      };

      axios
        .post(UPDATE_COMPLIANCE_RULE_URL, payload, {
          headers: this.headers,
        })
        .then(() => {
          this.show_success("Compliance rule updated successfully");
          this.close_rule_modal();
          this.get_compliance_rules();
        })
        .catch((error) => {
          this.show_error(
            error.response?.data?.message || "Failed to update rule"
          );
          if (error.response && error.response.status === 403) {
            this.refreshToken();
          }
        })
        .finally(() => {
          this.is_creating_rule = false;
        });
    },

    /* ---------------- DELETE RULE ---------------- */
    delete_rule() {
      if (this.is_deleting || !this.rule_to_delete) return;

      if (!this.token) {
        this.show_error("Authentication token is not available. Please refresh the page.");
        return;
      }

      this.is_deleting = true;

      const payload = {
        rule_id: this.rule_to_delete.id,
      };

      axios
        .post(DELETE_COMPLIANCE_RULE_URL, payload, {
          headers: this.headers,
        })
        .then(() => {
          this.show_success("Compliance rule deleted successfully");
          this.close_delete_modal();
          this.get_compliance_rules();
        })
        .catch((error) => {
          this.show_error(
            error.response?.data?.message || "Failed to delete rule"
          );
          if (error.response && error.response.status === 403) {
            this.refreshToken();
          }
        })
        .finally(() => {
          this.is_deleting = false;
        });
    },

    /* ---------------- UTILITY FUNCTIONS ---------------- */
    reset_rule_form() {
      this.rule_form = {
        id: null,
        name: "",
        description: "",
        initial_grace_period: "",
        frequency: "",
        recurring_grace_period: "",
        entity_type: sessionStorage.getItem("entity_type_id"),
        version: 1,
        initial_grace_period_days: null,
        recurring_grace_period_days: null,
        frequency_type: null,
        custom_frequency_days: null,
        trigger_status_ids: [],
      };
    },

    /* ---------------- FETCH FORM STATUSES ---------------- */
    fetch_form_statuses() {
      axios
        .get(GET_COMPLIANCE_DROPDOWNS_URL, {
          headers: this.headers,
        })
        .then((response) => {
          if (response.data?.status === "success") {
            this.form_status_options = response.data.data.form_statuses || [];
          }
        })
        .catch((error) => {
          console.warn("Error fetching form statuses:", error);
        });
    },

    /* ---------------- DATATABLE ---------------- */
    initialize_data_table() {
      const table_id = "#rule_management_table";

      this.$nextTick(() => {
        const table_element = document.querySelector(table_id);
        if (!table_element) return;

        if ($.fn.DataTable.isDataTable(table_id)) {
          $(table_id).DataTable().destroy();
        }

        this.dataTable = $(table_id).DataTable({
          responsive: true,
          autoWidth: false,
          pageLength: 10,
          columnDefs: [
            { orderable: false, targets: [6] }
          ]
        });
      });
    },
  },

  beforeUnmount() {
    if (this.createRuleModal) {
      this.createRuleModal.dispose();
    }
    if (this.deleteConfirmationModal) {
      this.deleteConfirmationModal.dispose();
    }
    if (this.assignQuestionsModal) {
      this.assignQuestionsModal.dispose();
    }

    if (this.dataTable) {
      this.dataTable.destroy();
    }
  },
};